import type { Cart, PaymentRequest } from 'models/api';
import { API_URL, fetchData } from 'utils/fetch';
import { createUrl } from 'utils/url';

import { isPaymentTypeWithComponentState } from '../helpers';

import type { PaymentMachineContext, PaymentResult } from './payment.types';

/**
 * Make request to start the payment flow.
 *
 * @see: https://apigw-fe-dev01.juladev.se/api/v1/docs/index.html#operations-Cart-post_api_v1_Cart__cartId__payment
 */
export function requestPayment(context: PaymentMachineContext) {
	const { cart } = context;
	if (!cart) {
		throw new Error('Cart is missing');
	}
	const selectedPaymentType = cart.selectedPayment?.selectedPaymentType;

	const paymentRequest: PaymentRequest = {
		// Don't use href since it may contain interfering query variables
		returnUrl: createUrl(`${globalThis.location.origin}/checkout/`, {
			cartId: cart.id,
			verifyRedirectPayment: 'true',
		}),
		paymentMethod: {
			type: cart.selectedPayment?.paymentMethodIdProvider,
		},
		storePaymentMethod: false,
		// https://docs.adyen.com/online-payments/3d-secure/api-reference/#browserinfo
		browserInfo: {
			// TODO: This should be done properly.
			acceptHeader: '*/*',
			colorDepth: globalThis.screen.colorDepth,
			// navigator.javaEnabled is deprecated and will always return false
			// https://developer.mozilla.org/en-US/docs/Web/API/Navigator/javaEnabled
			javaEnabled: false,
			language: navigator.language,
			screenHeight: globalThis.screen.height,
			screenWidth: globalThis.screen.width,
			timeZoneOffset: String(new Date().getTimezoneOffset()),
			// Can be spoofed, could possibly be removed as well.
			userAgent: navigator.userAgent || 'Unknown',
		},
	};

	const componentState = context.paymentComponentState;
	if (
		isPaymentTypeWithComponentState(selectedPaymentType) &&
		componentState?.isValid
	) {
		paymentRequest.paymentMethod = componentState.data?.paymentMethod;
	}

	return fetchData<PaymentResult>(`${API_URL}Cart/${cart.id}/payment`, {
		method: 'POST',
		body: JSON.stringify(paymentRequest),
	});
}

/**
 * Make request to finalize the payment (after payment is done).
 *
 * @see: https://apigw-fe-dev01.juladev.se/api/v1/docs/index.html#operations-Cart-post_api_v1_Cart__cartId__finalize
 */
export function finalizePayment(cartId: string) {
	return fetchData<Cart>(`${API_URL}Cart/${cartId}/finalize`, {
		method: 'POST',
	});
}
